import { WhatsAppOutlined } from '@ant-design/icons';
import { Button, Divider, FloatButton, Modal, Typography } from 'antd';
import { useState } from 'react';
import { resolveAssetUrl } from '../../../utils/resolveAssetUrl';
import IconLock from '../../Icons/Lock';
import IconProtect from '../../Icons/Protect';
import IconShield from '../../Icons/Shield';
import FooterDefault from '../../shared/FooterDefault';
import { useForm } from '../context';

const { Link } = Typography;

type ModalContent = { kind: 'html'; html: string } | { kind: 'url'; url: string };

const Footer = () => {
  const { checkout } = useForm();
  const hasSomeContact = checkout?.companyEmail || checkout?.companyPhone;

  const [modalContent, setModalContent] = useState<ModalContent | null>(null);

  const showHtml = (html: string) => setModalContent({ kind: 'html', html });
  const showUrl = (url: string) => setModalContent({ kind: 'url', url });
  const handleCancel = () => setModalContent(null);

  const hasAgreementTerms = !!checkout?.agreementTerms;
  const hasLegalDocuments = !!checkout?.legalDocuments;
  const isWhitelabel = !!checkout?.hasOrganization;
  const showRouterfyFallback = !hasAgreementTerms && !hasLegalDocuments && !isWhitelabel;

  const openAgreementTerms = () => {
    if (!checkout?.agreementTerms) return;
    if (checkout.agreementTerms.type === 'text') {
      showHtml(checkout.agreementTerms.value);
    } else {
      showUrl(checkout.agreementTerms.value);
    }
  };

  return (
    <div>
      <Modal
        title={
          <div
            className="mt-8 flex rounded-lg items-center justify-center bg-gray-100 py-6"
            style={{ backgroundColor: checkout?.styleConfig?.footerBgColor }}
          >
            <img alt="Logo" className="max-w-100" src={resolveAssetUrl(checkout?.companyLogo)} />
          </div>
        }
        open={!!modalContent}
        onCancel={handleCancel}
        style={{ minWidth: 320, maxWidth: 1200 }}
        width="100%"
        footer={[
          <Button key="ok" className="rounded-md w-20" type="default" onClick={handleCancel}>
            OK
          </Button>,
        ]}
      >
        {modalContent?.kind === 'html' && (
          <iframe
            className="w-full"
            style={{ height: '60vh', border: 'none' }}
            srcDoc={modalContent.html}
          />
        )}
        {modalContent?.kind === 'url' && (
          <iframe
            className="w-full"
            style={{
              height: '60vh',
              border: 'none',
            }}
            src={resolveAssetUrl(modalContent.url)}
          />
        )}
      </Modal>

      <div className="mt-2 flex flex-col sm:flex-row justify-center gap-5">
        <div className="flex items-center justify-center gap-1 text-green-600">
          <IconLock className="w-4.5" />
          <p className="text-sm">Privacidade protegida</p>
        </div>

        <div className="flex items-center justify-center gap-1 text-green-600">
          <IconShield className="w-4.5" />
          <p className="text-sm">Compra 100% segura</p>
        </div>

        <div className="flex items-center justify-center gap-1 text-green-600">
          <IconProtect className="w-4.5" />
          <p className="text-sm">Verificado e protegido</p>
        </div>
      </div>

      {hasSomeContact && (
        <div>
          <Divider variant='dashed' />
          <p className="text-base text-black">Precisa de ajuda?</p>
        </div>
      )}

      {checkout?.companyEmail && (
        <div>
          <p className="text-sm text-gray-600">
            Dúvidas sobre o produto: <span className="font-medium">{checkout?.companyEmail}</span>
          </p>
        </div>
      )}

      {checkout?.companyPhone && (
        <>
          <div>
            <Link
              className="text-sm"
              href={`https://api.whatsapp.com/send?phone=${checkout.companyPhone.replace(/\D/g, '')}`}
              target="_blank"
            >
              Preciso de ajuda para preencher esta tela
            </Link>
          </div>

          <FloatButton
            tooltip="Como posso ajudar?"
            type="primary"
            href={`https://api.whatsapp.com/send?phone=${checkout.companyPhone.replace(/\D/g, '')}`}
            target="_blank"
            icon={<WhatsAppOutlined />}
          />
        </>
      )}

      <Divider variant='dashed' className="my-6" />

      {!!checkout?.installmentFee && checkout?.installmentFee > 0 && (
        <div>
          <p className="text-xs text-gray-500 leading-relaxed">
            * O valor parcelado possui acréscimo de {checkout?.installmentFee / 100}% a.m.
          </p>
        </div>
      )}

      {hasAgreementTerms && (
        <div className="text-xs text-gray-500 leading-relaxed">
          Ao clicar em "Comprar Agora", eu declaro que li e concordo com os
          <Link className="text-xs ml-1" onClick={openAgreementTerms}>
            Termos de Uso, Política de Privacidade e Cookies{' '}
          </Link>
          e afirma que é maior de idade ou autorizado e acompanhado por um responsável legal.
        </div>
      )}

      {!hasAgreementTerms && hasLegalDocuments && (
        <div className="text-xs text-gray-500 leading-relaxed">
          Ao clicar em "Comprar Agora", eu declaro que li e concordo com o
          <Link className="text-xs ml-1" onClick={() => showHtml(checkout!.legalDocuments!.termsOfUse)}>
            Termos de Uso
          </Link>
          ,
          <Link className="text-xs ml-1" onClick={() => showHtml(checkout!.legalDocuments!.privacyPolicy)}>
            Política de Privacidade
          </Link>{' '}
          e
          <Link className="text-xs ml-1" onClick={() => showHtml(checkout!.legalDocuments!.cookiesPolicy)}>
            Cookies{' '}
          </Link>
          e afirma que é maior de idade ou autorizado e acompanhado por um responsável legal.
        </div>
      )}

      {showRouterfyFallback && (
        <div className="text-xs text-gray-500 leading-relaxed">
          Ao clicar em "Comprar Agora", eu declaro que li e concordo com o
          <Link className="text-xs ml-1" onClick={() => showUrl('https://routerfy.com/termosuso')}>
            Termos de Uso
          </Link>
          ,
          <Link className="text-xs ml-1" onClick={() => showUrl('https://routerfy.com/politicaprivacidade')}>
            Política de Privacidade
          </Link>{' '}
          e
          <Link className="text-xs ml-1" onClick={() => showUrl('https://routerfy.com/politicacookies')}>
            Cookies{' '}
          </Link>
          e afirma que é maior de idade ou autorizado e acompanhado por um responsável legal.
        </div>
      )}

      <FooterDefault className='mt-4' logo={checkout?.companyLogo} bgColor={checkout?.styleConfig?.footerBgColor} />
    </div>
  );
};

export default Footer;
